import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { sharedLib } from "@shared/services/shared.service";
import { LOCAL_STORAGE_REGISTRATION_DATA_KEY } from "@shared/common/constants";
import { LanguageNameSpaces, OAuthPlatformType } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useQuery from "utils/hooks/useQuery";
import useSocialLogins from "features/auth/useSocialLogins";
import { localStorageService } from "utils/localStorageService";
import { displayResultMessage } from "utils/misc";
import {
  DISCORD_LINK_CODE_QUERY_PARAM,
  SESSION_STORAGE_DISCORD_LINK_RETURN_KEY,
} from "common/constants";

// Finishes a "Connect with Discord" round trip: the backend sends the member
// back with a one-time code, which links Discord to the signed-in user.
export const useConsumeDiscordLinkCode = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const code = query.get(DISCORD_LINK_CODE_QUERY_PARAM);
  const consumedCode = useRef<string | null>(null);
  const [connectDiscordOAuth] =
    sharedLib.sharedApi.authentication.useConnectDiscordOAuthMutation();

  useEffect(() => {
    if (!code || consumedCode.current === code) return;
    consumedCode.current = code;
    const returnPath = popReturnPath();
    query.delete(DISCORD_LINK_CODE_QUERY_PARAM);
    history.replace({ pathname: location.pathname, search: query.toString() });
    displayResultMessage(
      connectDiscordOAuth({ code }).unwrap(),
      t("external_account.discord.success_message")
    )
      .then(() => {
        // A pending signal from a Discord button wins: useConsumeSearchParams
        // opens it once the user shows Discord as connected.
        const hasPendingSignal = !!localStorageService.getValue(
          LOCAL_STORAGE_REGISTRATION_DATA_KEY
        );
        if (
          returnPath &&
          !hasPendingSignal &&
          returnPath !== location.pathname + location.search
        )
          history.push(returnPath);
      })
      .catch(() => {});
  }, [code]);
};

// Sends the signed-in member to Discord to approve linking, remembering
// where to bring them back.
export const useStartDiscordLink = () => {
  const { onLoginClick } = useSocialLogins({
    openUrl: (url) => window.open(url, "_self"),
  });

  return () => {
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_DISCORD_LINK_RETURN_KEY,
        window.location.pathname + window.location.search
      );
    } catch {
      // Without sessionStorage the member stays on the page Discord returns to.
    }
    return onLoginClick(OAuthPlatformType.DISCORD, true);
  };
};

const popReturnPath = () => {
  try {
    const path = sessionStorage.getItem(
      SESSION_STORAGE_DISCORD_LINK_RETURN_KEY
    );
    sessionStorage.removeItem(SESSION_STORAGE_DISCORD_LINK_RETURN_KEY);
    return path?.startsWith("/") ? path : null;
  } catch {
    return null;
  }
};
